import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Button, Divider, Form } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import Dialog from '~/components/Dialog'
import notify from '~/components/Notify'

interface IrrigationEventModalProps {
  open: boolean
  onClose: () => void
  eventIndex: number
  waterSourceOptions: Array<{ key: number; value: number; text: string }>
  powerSourceOptions: Array<{ key: number; value: number; text: string }>
  depthUnitOptions: Array<{ key: number; value: number; text: string }>
  distanceUnitOptions: Array<{ key: number; value: number; text: string }>,
  handleUpdate: (value: any) => void
}

const defaultValues = {
  depth: '',
  depthUnit: '',
  distance: '',
  distanceUnit: '',
  powerSource: '',
  waterSource: ''
}

const defaultValueKeys = [
  'defaultIrrigationWaterSource',
  'defaultIrrigationDepth',
  'defaultIrrigationDepthUnit',
  'defaultIrrigationDistance',
  'defaultIrrigationDistanceUnit',
  'defaultIrrigationPowerSource'
]

const IrrigationEventModal: React.FC<IrrigationEventModalProps> = (props) => {
  const {
    open,
    onClose,
    eventIndex,
    waterSourceOptions,
    powerSourceOptions,
    depthUnitOptions,
    distanceUnitOptions,
    handleUpdate
  } = props
  const { t } = useTranslation()
  const { getValues } = useFormContext()

  const [irrigationEventData, setIrrigationEventData] = useState(null)

  const data = getValues()
  const hideResetButton = defaultValueKeys.find(key => !data[key])

  useEffect(() => {
    if(eventIndex >= 0) {
      const eventValues = getValues(`irrigationEvents[${eventIndex}]`)
      const defaultIrrigation = defaultValues
      Object.keys(defaultIrrigation).map(key => {
        defaultIrrigation[key] = eventValues[key]
      })
      setIrrigationEventData(defaultIrrigation)
    }
  }, [eventIndex])

  function handleResetToDefault() {
    setIrrigationEventData({
      depth: data.defaultIrrigationDepth,
      depthUnit: data.defaultIrrigationDepthUnit,
      distance: data.defaultIrrigationDistance,
      distanceUnit: data.defaultIrrigationDistanceUnit,
      powerSource: data.defaultIrrigationPowerSource,
      waterSource: data.defaultIrrigationWaterSource
    })
  }

  const renderContent = () => {
    return (
      <Form>
        <Form.Group style={{ alignItems: 'end' }}>
          <Form.Input
            width={'10'}
            label={t('Pumping depth/height')}
            placeholder={t('Enter depth')}
            type='number'
            value={irrigationEventData?.depth}
            onChange={(e, data) => setIrrigationEventData({
              ...irrigationEventData,
              depth: data.value
            })}
          />
          <Form.Select
            options={depthUnitOptions}
            width={6}
            selectOnBlur={false}
            placeholder={t('Select unit')}
            value={irrigationEventData?.depthUnit}
            onChange={(e, data) => setIrrigationEventData({
              ...irrigationEventData,
              depthUnit: data.value as string
            })}
          />
        </Form.Group>
        <Form.Group style={{ alignItems: 'end' }}>
          <Form.Input
            width={'10'}
            label={t('Horizontal distance')}
            placeholder={t('Enter distance')}
            type='number'
            value={irrigationEventData?.distance}
            onChange={(e, data) => setIrrigationEventData({
              ...irrigationEventData,
              distance: data.value
            })}
          />
          <Form.Select
            options={distanceUnitOptions}
            width={6}
            selectOnBlur={false}
            placeholder={t('Select unit')}
            value={irrigationEventData?.distanceUnit}
            onChange={(e, data) => setIrrigationEventData({
              ...irrigationEventData,
              distanceUnit: data.value as string
            })}
          />
        </Form.Group>
        <Form.Group widths={'equal'}>
          <Form.Select
            options={powerSourceOptions}
            selectOnBlur={false}
            label={t('Power source')}
            placeholder={t('Select power source')}
            value={irrigationEventData?.powerSource}
            onChange={(e, data) => setIrrigationEventData({
              ...irrigationEventData,
              powerSource: data.value as string
            })}
          />
          <Form.Select
            options={waterSourceOptions}
            selectOnBlur={false}
            label={t('Water source')}
            placeholder={t('Select water source')}
            value={irrigationEventData?.waterSource}
            onChange={(e, data) => setIrrigationEventData({
              ...irrigationEventData,
              waterSource: data.value as string
            })}
          />
        </Form.Group>
        {!hideResetButton && <div style={{ display: 'flex', justifyContent: 'end', marginTop: '1em' }}>
          <Button onClick={() => handleResetToDefault()} primary type='button'>{t('Reset to defaults')}</Button>
          </div>}
      </Form>
    )
  }

  function handleSubmit() {
    const errors = Object.values(irrigationEventData).filter(value => !value)
    if(errors.length > 0) {
      notify(t('Please fill in all fields'), 'error')
      return
    }
    setIrrigationEventData(defaultValues)
    handleUpdate(irrigationEventData)
  }

  return (
    <Dialog
      textHeader={t('Irrigation event')}
      textClose={t('Cancel')}
      textSubmit={t('Change')}
      style={{ width: 800, padding: '1em' }}
      noScrollingContent
      isDialogOpen={open && Boolean(irrigationEventData)}
      handleClose={() => {
        setIrrigationEventData(defaultValues)
        onClose()
      }}
      handleSubmit={() => handleSubmit()}
      textContent={renderContent()}
    />
  )
}

export default IrrigationEventModal
